import { NodeDataBuilder, NodeUtils } from 'Editor/services/DataManager';
import { BaseManipulator } from '../Base';
import { InsertElementOperation } from 'Editor/services/Edition_v2/Operations';
import { StylesDataMapper } from './StylesDataMapper';

export abstract class BaseStylesManipulator extends BaseManipulator {
  protected buildFormatData<T extends Editor.Edition.InlineStyles>(
    childNodes: Editor.Data.Node.Data[],
    style: T,
    value: Editor.Edition.StylesMap[T],
  ) {
    const builder = new NodeDataBuilder('format');

    builder.addChildData(...childNodes);

    const data = builder.build();

    if (data) {
      StylesDataMapper.InlineData[style].addStyleValue(data, value);
    }

    return data;
  }

  protected hasFormatAnyStyle(formatData: Editor.Data.Node.FormatData) {
    const propKeys = Object.keys(formatData.properties);
    return propKeys.length > 0;
  }

  // TODO: move set to  an operation?
  protected updateInlineStyleOperation<T extends Editor.Edition.InlineStyles>(
    baseModel: Editor.Data.Node.Model,
    elementPathToUpdate: Editor.Selection.Path,
    style: T,
    value: Editor.Edition.StylesMap[T],
  ) {
    const propPath = StylesDataMapper.InlineData[style].getStylePropertyPath();
    const processedValue = StylesDataMapper.InlineData[style].processValue(value);

    baseModel.set([...elementPathToUpdate, ...propPath], processedValue, {
      source: 'LOCAL_RENDER',
    });
  }

  // TODO: move delete to  an operation?
  protected removeInlineStyleOperation<T extends Editor.Edition.InlineStyles>(
    baseModel: Editor.Data.Node.Model,
    elementPathToUpdate: Editor.Selection.Path,
    style: T,
  ) {
    const propPath = StylesDataMapper.InlineData[style].getStylePropertyPath();

    baseModel.delete([...elementPathToUpdate, ...propPath], {
      source: 'LOCAL_RENDER',
    });
  }

  protected canMergeFormat(format1: Editor.Data.Node.Data, format2: Editor.Data.Node.Data) {
    return (
      NodeUtils.isFormatData(format1) &&
      NodeUtils.isFormatData(format2) &&
      format1.properties.bold === format2.properties.bold &&
      format1.properties.color === format2.properties.color &&
      format1.properties.fontfamily === format2.properties.fontfamily &&
      format1.properties.fontsize === format2.properties.fontsize &&
      format1.properties.backgroundcolor === format2.properties.backgroundcolor &&
      format1.properties.italic === format2.properties.italic &&
      format1.properties.strikethrough === format2.properties.strikethrough &&
      format1.properties.superscript === format2.properties.superscript &&
      format1.properties.subscript === format2.properties.subscript &&
      format1.properties.underline === format2.properties.underline &&
      format1.properties.v === format2.properties.v
    );
  }

  protected getClosestWithStyle<T extends Editor.Edition.InlineStyles>(
    baseData: Editor.Data.Node.Data,
    childPath: Editor.Selection.Path,
    style: T,
    value?: Editor.Edition.StylesMap[T],
  ) {
    if (!this.editionContext.stylesManager) {
      return null;
    }

    let closestFormat = NodeUtils.closestOfTypeByPath(baseData, childPath, ['format']);

    while (closestFormat && NodeUtils.isFormatData(closestFormat.data)) {
      // process both values to make sure we are comparing the same data type

      const appliedProp = StylesDataMapper.InlineData[style].processValue(
        this.editionContext.stylesManager.InlineStylesParser[style](closestFormat.data),
      );

      const processValue = StylesDataMapper.InlineData[style].processValue(value);

      if (processValue != null) {
        // check is has style and processValue
        if (appliedProp === processValue) {
          return closestFormat;
        }
      } else if (appliedProp != null) {
        // check if has only style
        return closestFormat;
      }

      let parentPath = closestFormat.path.slice(0, closestFormat.path.length - 2);
      if (parentPath.length > 0) {
        closestFormat = NodeUtils.closestOfTypeByPath(baseData, parentPath, ['format']);
      } else {
        closestFormat = null;
      }
    }

    return null;
  }

  protected getSylableDataInfo(range: Editor.Selection.JsonRange) {
    if (!this.editionContext.DataManager) {
      return [];
    }

    return range.getNodes(
      this.editionContext.DataManager,
      ['text', 'format', ...NodeUtils.INLINE_NON_STYLABLE_TYPES],
      (
        baseData: Editor.Data.Node.Data,
        childData: Editor.Data.Node.Data,
        childPath: Editor.Selection.Path,
      ) => {
        // validation function

        // empty format data
        if (NodeUtils.isFormatData(childData) && childData.childNodes?.length === 0) {
          return true;
        }

        // text data
        if (NodeUtils.isTextData(childData)) {
          return !NodeUtils.closestOfTypeByPath(
            baseData,
            childPath,
            NodeUtils.INLINE_NON_STYLABLE_TYPES,
          );
        }

        // non text data
        if (NodeUtils.isNonStylableInlindeData(childData)) {
          return true;
        }

        return false;
      },
    );
  }

  protected insertElement(
    baseModel: Editor.Data.Node.Model,
    insertElement: Editor.Data.Node.Data,
    insertPath: Editor.Selection.Path,
  ): Editor.Selection.Path | undefined {
    // do not insert empty elements
    if (
      NodeUtils.isFormatData(insertElement) &&
      insertElement.childNodes &&
      insertElement.childNodes.length === 0
    ) {
      return;
    }

    const baseData = baseModel.selectedData();

    if (!baseData) {
      return;
    }

    let workingPath: Editor.Selection.Path | undefined = [...insertPath];

    let parentChildInfo = NodeUtils.getParentChildInfoByPath(baseData, workingPath);

    if (!parentChildInfo) {
      return;
    }

    const previousSibling = NodeUtils.getPreviousSibling(baseData, workingPath);
    const nextSibling = NodeUtils.getNextSibling(baseData, workingPath);

    let isAtStart = false;
    let isAtEnd = false;
    if (NodeUtils.isTextData(parentChildInfo.childData) && parentChildInfo.contentIndex != null) {
      isAtStart = parentChildInfo.contentIndex === 0;
      isAtEnd = parentChildInfo.contentIndex === parentChildInfo.childData.content.length;
    } else {
      isAtStart = parentChildInfo.childIndex === 0;
      isAtEnd = parentChildInfo.childIndex === (parentChildInfo.parentData.childNodes?.length || 0);
    }

    if (isAtStart && previousSibling && this.canMergeFormat(previousSibling.data, insertElement)) {
      // has previous sibling format and can be merge
      let length = previousSibling.data.childNodes?.length || 0;
      workingPath = [...previousSibling.path, 'childNodes', length];

      const childNodes = insertElement.childNodes || [];
      for (let c = 0; c < childNodes.length; c++) {
        if (workingPath) {
          let insertOp: Editor.Edition.IOperationBuilder = new InsertElementOperation(
            baseModel,
            workingPath,
            childNodes[c],
            {
              pathFix: 'AFTER',
            },
          );
          insertOp.apply();
          workingPath = insertOp.getAdjustedPath();
        }
      }
    } else if (isAtEnd && nextSibling && this.canMergeFormat(nextSibling.data, insertElement)) {
      // has next sibling format and can be merge
      workingPath = [...nextSibling.path, 'childNodes', 0];

      const childNodes = insertElement.childNodes || [];
      for (let c = 0; c < childNodes.length; c++) {
        if (workingPath) {
          let insertOp: Editor.Edition.IOperationBuilder = new InsertElementOperation(
            baseModel,
            workingPath,
            childNodes[c],
            {
              pathFix: 'AFTER',
              mergeText: false,
            },
          );
          insertOp.apply();
          workingPath = insertOp.getAdjustedPath();
        }
      }
    } else {
      let insertOp = new InsertElementOperation(baseModel, workingPath, insertElement, {
        pathFix: 'AFTER',
        mergeText: false,
      });
      insertOp.apply();
      workingPath = insertOp.getAdjustedPath();
    }

    return workingPath;
  }
}
